import React, { useContext, useState } from 'react';
import {
  App, Button, DatePicker, Dropdown, Input, Space, Tag,
} from 'antd';
import {
  ClockCircleOutlined, CloseOutlined, DownOutlined, SendOutlined,
} from '@ant-design/icons';
import { useMediaQuery } from 'usehooks-ts';
import { captureException } from '@sentry/react';
import useHttpClient from '/src/hooks/useHttpClient';
import ResponsiveModal from '/src/components/ResponsiveModal';
import { UploadContext } from '/src/constants/contexts';
import { UPLOAD_API_BASE_URL } from '/src/constants';

const ONE_DAY = 24 * 60 * 60 * 1000;

// Normalize whatever the active picker produced to an ISO string:
// the desktop antd DatePicker yields a dayjs object, the mobile native
// <input type="datetime-local"> yields a local "YYYY-MM-DDTHH:mm" string.
function toIso(value) {
  if (!value) {
    return null;
  }
  return typeof value === 'string' ? new Date(value).toISOString() : value.toISOString();
}

// Local-time "YYYY-MM-DDTHH:mm" for the native input's min attribute.
function localDateTimeValue(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function PublishButton({ onChange }) {
  const { message } = App.useApp();
  const httpClient = useHttpClient({ baseURL: UPLOAD_API_BASE_URL });
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [loading, setLoading] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  // dayjs (desktop DatePicker) or "YYYY-MM-DDTHH:mm" string (mobile native input).
  const [scheduledAt, setScheduledAt] = useState(null);
  const { isUploading, isUploaded, video } = useContext(UploadContext);

  function publish(scheduledPublishAt) {
    setLoading(true);
    return httpClient
      .put(`videos/${video.id}/publish`, scheduledPublishAt ? { scheduledPublishAt } : {})
      .then(onChange)
      .catch((error) => {
        if (error?.response?.data?.error === 'SCHEDULE_IN_PAST') {
          message.error('زمان انتخاب‌شده برای انتشار باید در آینده باشد.');
        } else {
          captureException(error);
          message.error('خطا هنگام انتشار!');
        }
        throw error;
      })
      .finally(() => setLoading(false));
  }

  function handlePublish() {
    publish().catch(() => {});
  }

  function handleUnpublish() {
    setLoading(true);
    httpClient.put(`videos/${video.id}/unpublish`).then(onChange)
      .catch(captureException)
      .finally(() => setLoading(false));
  }

  function confirmSchedule() {
    const iso = toIso(scheduledAt);
    if (!iso) {
      return;
    }
    // Guard against picking an earlier time *today* (the picker only disables
    // whole past days), so the user gets an accurate message without a round-trip.
    if (new Date(iso).getTime() <= Date.now()) {
      message.error('زمان انتخاب‌شده برای انتشار باید در آینده باشد.');
      return;
    }
    publish(iso)
      .then(() => {
        setScheduleOpen(false);
        setScheduledAt(null);
      })
      .catch(() => {});
  }

  const scheduledFuture = video.scheduledPublishAt
    && new Date(video.scheduledPublishAt).getTime() > Date.now();

  if (['PUBLISHED', 'EXPIRED'].includes(video.state) || video.fileState === 'UPLOAD_FAILED') {
    if (video.state === 'PUBLISHED' && scheduledFuture) {
      return (
        <Tag bordered={false} icon={<ClockCircleOutlined />} color="processing">
          {`انتشار در یوتیوب: ${new Date(video.scheduledPublishAt).toLocaleString('fa-IR')}`}
        </Tag>
      );
    }
    return null;
  }

  if (video.state === 'PUBLISHING') {
    return (
      <Button loading disabled size="large" block={isMobile}>
        در حال انتشار
      </Button>
    );
  }

  if (video.state === 'WAITING_FOR_PUBLISH') {
    if (isUploaded) {
        return (
          <Button loading disabled size="large" block={isMobile}>
            در صف انتشار
          </Button>
      );
    }
    return (
      <Button loading={loading} icon={<CloseOutlined />} onClick={handleUnpublish} size="large" block={isMobile}>
        لغو انتشار خودکار
      </Button>
    );
  }

  const publishDisabled = !isUploaded && !isUploading;
  const type = isUploaded ? 'primary' : 'default';
  const scheduleMenu = {
    items: [{ key: 'schedule', icon: <ClockCircleOutlined />, label: 'زمان‌بندی انتشار' }],
    onClick: () => setScheduleOpen(true),
  };

  return (
    <>
      <Space.Compact style={isMobile ? { width: '100%' } : undefined}>
        <Button
          icon={<SendOutlined />}
          loading={loading}
          disabled={publishDisabled}
          type={type}
          onClick={handlePublish}
          size="large"
          style={isMobile ? { flex: 1, justifyContent: 'center' } : undefined}
        >
          {isUploading ? 'انتشار پس از تکمیل آپلود' : 'انتشار'}
        </Button>
        <Dropdown
          menu={scheduleMenu}
          trigger={['click']}
          placement={isMobile ? 'topRight' : 'bottomRight'}
          disabled={publishDisabled || loading}
        >
          <Button type={type} size="large" icon={<DownOutlined />} />
        </Dropdown>
      </Space.Compact>

      <ResponsiveModal
        title="زمان‌بندی انتشار"
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        onOk={confirmSchedule}
        okText="زمان‌بندی"
        cancelText="انصراف"
        confirmLoading={loading}
        okDisabled={!scheduledAt}
      >
        <p className="mb-4">
          ویدیو بلافاصله به‌صورت خصوصی روی یوتیوب آپلود می‌شود و در زمان انتخاب‌شده به‌صورت عمومی منتشر می‌گردد.
        </p>
        {isMobile ? (
          // Compact native picker on mobile — uses the OS date/time UI instead
          // of antd's large popup panel inside the bottom sheet.
          <Input
            type="datetime-local"
            size="large"
            value={typeof scheduledAt === 'string' ? scheduledAt : ''}
            min={localDateTimeValue(new Date())}
            onChange={(e) => setScheduledAt(e.target.value)}
          />
        ) : (
          <DatePicker
            className="w-full"
            showTime
            value={scheduledAt}
            onChange={setScheduledAt}
            placeholder="انتخاب تاریخ و زمان انتشار"
            disabledDate={(d) => d.valueOf() < Date.now() - ONE_DAY}
            getPopupContainer={(trigger) => trigger.parentElement}
          />
        )}
      </ResponsiveModal>
    </>
  );
}

export default PublishButton;
